<template>
  <div v-if="isVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center">
          <h3 class="text-lg font-semibold text-gray-900">
            Configure Invalid Filenames
          </h3>
        </div>
        <p class="text-sm text-gray-600 mt-2">
          <span v-if="modalStep === 'filename-correction'">Fix filename format issues first, then resolve duplicate SubIDs.</span>
          <span v-else-if="modalStep === 'duplicate-resolution'">Review and resolve any duplicate SubIDs found.</span>
          <span v-else>Review the approved files that will be processed.</span>
        </p>
      </div>

      <!-- Content -->
      <div class="px-6 py-4 overflow-y-auto max-h-[60vh]">
        <!-- Step 1: Filename Correction -->
        <div v-if="modalStep === 'filename-correction'" class="space-y-4">
          <h4 class="text-md font-semibold text-gray-800 border-b border-gray-200 pb-2">
            Fix Filename Format Issues
          </h4>
          <p class="text-sm text-gray-600 mb-4">
            These files have invalid filename formats. Please provide the correct SubID and Dataset ID, or exclude them.
          </p>
          
          <div
            v-for="(file, index) in filesNeedingCorrection"
            :key="index"
            class="border rounded-lg p-4 transition-all duration-200"
            :class="{ 
              'opacity-50': excludedFiles.includes(file.filename),
              'border-gray-200 bg-white': !isValidSubid(file.subid) || !isValidDatasetId(file.datasetId),
              'border-green-200 bg-green-50': isValidSubid(file.subid) && isValidDatasetId(file.datasetId) && !excludedFiles.includes(file.filename)
            }"
          >
            <div class="flex items-start justify-between mb-3">
              <div class="flex items-center space-x-3">
                <!-- Icon changes based on validation state -->
                <svg v-if="isValidSubid(file.subid) && isValidDatasetId(file.datasetId) && !excludedFiles.includes(file.filename)" 
                     class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <svg v-else class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div>
                  <h4 class="font-medium" :class="{ 
                    'text-gray-900': !isValidSubid(file.subid) || !isValidDatasetId(file.datasetId),
                    'text-green-800': isValidSubid(file.subid) && isValidDatasetId(file.datasetId) && !excludedFiles.includes(file.filename)
                  }">
                    {{ file.filename }}
                  </h4>
                  <p v-if="(!isValidSubid(file.subid) || !isValidDatasetId(file.datasetId)) && !excludedFiles.includes(file.filename)" class="text-sm text-red-600">
                    {{ file.reason }}
                  </p>
                </div>
              </div>
              <label class="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  :checked="!excludedFiles.includes(file.filename)"
                  @change="toggleFileExclusion(file.filename)"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span class="text-sm text-gray-700">Include</span>
              </label>
            </div>

            <div v-if="!excludedFiles.includes(file.filename)" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  SubID (3-6 digits)
                </label>
                <input
                  v-model="file.subid"
                  type="text"
                  placeholder="e.g., 123"
                  class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-colors"
                  :class="{ 
                    'border-red-500 focus:ring-red-500': file.subid && !isValidSubid(file.subid),
                    'border-green-500 focus:ring-green-500': file.subid && isValidSubid(file.subid),
                    'border-gray-300 focus:ring-blue-500': !file.subid
                  }"
                />
                <p v-if="file.subid && !isValidSubid(file.subid)" class="text-xs text-red-600 mt-1">
                  SubID must be 3-6 digits
                </p>
                <p v-else-if="file.subid && isValidSubid(file.subid)" class="text-xs text-green-600 mt-1 flex items-center space-x-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Valid SubID</span>
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Dataset ID (3 digits)
                </label>
                <input
                  v-model="file.datasetId"
                  type="text"
                  placeholder="e.g., 001"
                  class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-colors"
                  :class="{ 
                    'border-red-500 focus:ring-red-500': file.datasetId && !isValidDatasetId(file.datasetId),
                    'border-green-500 focus:ring-green-500': file.datasetId && isValidDatasetId(file.datasetId),
                    'border-gray-300 focus:ring-blue-500': !file.datasetId
                  }"
                />
                <p v-if="file.datasetId && !isValidDatasetId(file.datasetId)" class="text-xs text-red-600 mt-1">
                  Dataset ID must be exactly 3 digits
                </p>
                <p v-else-if="file.datasetId && isValidDatasetId(file.datasetId)" class="text-xs text-green-600 mt-1 flex items-center space-x-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Valid Dataset ID</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Duplicate Resolution -->
        <div v-if="modalStep === 'duplicate-resolution'" class="space-y-4">
          <h4 class="text-md font-semibold text-orange-800 border-b border-orange-200 pb-2 flex items-center space-x-2">
            <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <span>Resolve Duplicate SubID-Dataset Combinations</span>
          </h4>
          <p class="text-sm text-orange-700 mb-4">
            The following SubID-Dataset combinations have multiple files. Choose which file to use for each combination, or exclude them entirely.
          </p>
          
          <div v-if="duplicateSubidDatasets.length === 0" class="text-center py-8">
            <svg class="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-green-600 font-medium">No duplicate SubID-Dataset combinations found!</p>
            <p class="text-gray-600 text-sm">All files have unique SubID-Dataset combinations.</p>
          </div>
          
          <div
            v-for="key in duplicateSubidDatasets"
            :key="key"
            class="border border-orange-200 rounded-lg p-4 bg-orange-50"
          >
            <div class="flex items-center space-x-3 mb-3">
              <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <h5 class="font-semibold text-orange-800">SubID-Dataset: {{ key.replace('_', ' - Dataset ') }}</h5>
            </div>
            
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">
                Select which file to use for {{ key.replace('_', ' - Dataset ') }}:
              </label>
              
              <div class="space-y-2">
                <!-- Exclude option -->
                <label class="flex items-center space-x-3 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    :name="`subid-dataset-${key}`"
                    :value="'exclude'"
                    v-model="subidDatasetChoices[key]"
                    class="text-red-600 focus:ring-red-500"
                  />
                  <div class="flex items-center space-x-2">
                    <svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <span class="text-sm text-red-700 font-medium">Exclude all files for this combination</span>
                  </div>
                </label>
                
                <!-- File options -->
                <label
                  v-for="file in filesWithDuplicates[key]"
                  :key="file.filename"
                  class="flex items-center space-x-3 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    :name="`subid-dataset-${key}`"
                    :value="file.filename"
                    v-model="subidDatasetChoices[key]"
                    class="text-blue-600 focus:ring-blue-500"
                  />
                  <div class="flex-1">
                    <div class="font-medium text-gray-900">{{ file.filename }}</div>
                    <div class="text-xs text-gray-600">SubID: {{ file.subid }}, Dataset ID: {{ file.datasetId }}</div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Review Approved Files -->
        <div v-if="modalStep === 'review-approved'" class="space-y-4">
          <h4 class="text-md font-semibold text-green-800 border-b border-green-200 pb-2 flex items-center space-x-2">
            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Review Approved Files</span>
          </h4>
          <p class="text-sm text-green-700 mb-4">
            Review the files that will be processed. You can exclude any files you don't want to include.
          </p>
          
          <div v-if="approvedFiles.length === 0" class="text-center py-8">
            <svg class="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <p class="text-gray-600 font-medium">No approved files found</p>
            <p class="text-gray-500 text-sm">All files were excluded or had issues.</p>
          </div>
          
          <div class="space-y-3">
            <div
              v-for="file in approvedFiles"
              :key="file.filename"
              class="border border-green-200 rounded-lg p-4 bg-green-50"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <div>
                    <div class="font-medium text-green-800">{{ file.filename }}</div>
                    <div class="text-sm text-green-600">SubID: {{ file.subid }}, Dataset: {{ file.datasetId }}</div>
                  </div>
                </div>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="approvedFileChoices[file.filename] !== false"
                    @change="toggleApprovedFile(file.filename)"
                    class="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span class="text-sm text-gray-700 font-medium">Include</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-between">
        <!-- Left side: Back buttons -->
        <div>
          <button
            v-if="modalStep === 'duplicate-resolution' && needsFilenameCorrection"
            @click="backToFilenameCorrection"
            class="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            <span>Back to Filename Correction</span>
          </button>
          <button
            v-if="modalStep === 'review-approved' && (needsDuplicateResolution || needsFilenameCorrection)"
            @click="backToDuplicateResolution"
            class="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            <span>{{ needsDuplicateResolution ? 'Back to Duplicate Resolution' : 'Back to Filename Correction' }}</span>
          </button>
        </div>
        
        <!-- Right side: Action buttons -->
        <div class="flex space-x-3">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          
          <!-- Filename correction: Proceed to next step -->
          <button
            v-if="modalStep === 'filename-correction'"
            @click="proceedToDuplicates"
            :disabled="!canProceedToDuplicates"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ needsDuplicateResolution ? 'Next: Check Duplicates' : 'Next: Review Files' }}
          </button>
          
          <!-- Duplicate resolution: Proceed to review -->
          <button
            v-if="modalStep === 'duplicate-resolution'"
            @click="proceedToReview"
            :disabled="!canProceedToReview"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next: Review Approved Files
          </button>
          
          <!-- Review approved: Final confirmation -->
          <button
            v-if="modalStep === 'review-approved'"
            @click="handleFinalConfirm"
            :disabled="!canFinalConfirm"
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CohortFilenameCorrectionModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    files: {
      type: Array,
      default: () => []
    },
    validFiles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localFiles: [],
      duplicateSubidDatasets: [],
      subidDatasetChoices: {}, // Maps "subid_dataset" to selected filename
      modalStep: 'filename-correction', // 'filename-correction', 'duplicate-resolution', or 'review-approved'
      excludedFiles: [], // Track files that user wants to exclude
      approvedFileChoices: {} // Track which approved files are included (filename -> boolean)
    }
  },
  computed: {
    canProceedToDuplicates() {
      // For step 1: All non-excluded files must have valid SubID and Dataset ID
      return this.filesNeedingCorrection.every(file => 
        this.excludedFiles.includes(file.filename) || 
        (this.isValidSubid(file.subid) && this.isValidDatasetId(file.datasetId))
      );
    },
    
    canConfirm() {
      // For step 2: All duplicate SubID-Dataset combinations must be resolved
      return Object.keys(this.subidDatasetChoices).length === this.duplicateSubidDatasets.length;
    },
    
    canProceedToReview() {
      // For step 2: All duplicate SubID-Dataset combinations must be resolved
      return Object.keys(this.subidDatasetChoices).length === this.duplicateSubidDatasets.length;
    },
    
    canFinalConfirm() {
      // For step 3: At least one approved file must be selected
      return Object.values(this.approvedFileChoices).some(included => included);
    },
    
    hasInvalidFilenames() {
      // Check if there are any files that need filename correction
      return this.filesNeedingCorrection.length > 0;
    },
    
    hasDuplicates() {
      // Check if there are any duplicate SubID-Dataset combinations
      return this.duplicateSubidDatasets.length > 0;
    },
    
    needsFilenameCorrection() {
      // Step 1 is needed if there are invalid filenames
      return this.hasInvalidFilenames;
    },
    
    needsDuplicateResolution() {
      // Step 2 is needed if there are duplicates
      return this.hasDuplicates;
    },
    
    needsReview() {
      // Step 3 is always needed to show approved files
      return this.approvedFiles.length > 0;
    },
    
    getInitialStep() {
      // Determine which step to show first based on what issues exist
      if (this.needsFilenameCorrection) {
        return 'filename-correction';
      } else if (this.needsDuplicateResolution) {
        return 'duplicate-resolution';
      } else {
        return 'review-approved';
      }
    },
    
    approvedFiles() {
      // Use validFiles prop if localFiles is empty (direct review case)
      const sourceFiles = this.localFiles.length > 0 ? this.localFiles : this.validFiles;
      
      // Get all files that are valid and not excluded
      const validFiles = sourceFiles.filter(file => 
        this.isValidSubid(file.subid) && 
        this.isValidDatasetId(file.datasetId) && 
        !this.excludedFiles.includes(file.filename)
      );
      
      // Filter out files that were handled in duplicate resolution
      const duplicateKeys = this.duplicateSubidDatasets;
      const nonDuplicateFiles = validFiles.filter(file => {
        const key = `${file.subid}_${file.datasetId}`;
        return !duplicateKeys.includes(key);
      });
      
      // Add files that were selected in duplicate resolution
      const selectedDuplicateFiles = [];
      Object.entries(this.subidDatasetChoices).forEach(([key, selectedFilename]) => {
        if (selectedFilename !== 'exclude') {
          const selectedFile = sourceFiles.find(f => f.filename === selectedFilename && `${f.subid}_${f.datasetId}` === key);
          if (selectedFile) {
            selectedDuplicateFiles.push(selectedFile);
          }
        }
      });
      
      return [...nonDuplicateFiles, ...selectedDuplicateFiles];
    },
    
    filesNeedingCorrection() {
      // Files that originally had filename format issues (keep them visible throughout the process)
      return this.localFiles.filter(file => {
        // Show files that originally had invalid filename format or missing SubID/Dataset ID
        // This includes files that have been corrected but should remain visible
        const originallyInvalidFormat = file.reason && file.reason.includes('Invalid filename format');
        const missingIds = !file.subid || !file.datasetId;
        return originallyInvalidFormat || missingIds;
      });
    },
    
    filesWithDuplicates() {
      const grouped = {};
      this.localFiles.forEach(file => {
        // Only include files that have valid SubID and Dataset ID and are not excluded
        if (file.subid && 
            this.isValidSubid(file.subid) && 
            this.isValidDatasetId(file.datasetId) && 
            !this.excludedFiles.includes(file.filename)) {
          const key = `${file.subid}_${file.datasetId}`;
          if (this.duplicateSubidDatasets.includes(key)) {
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(file);
          }
        }
      });
      return grouped;
    },
    
    filesWithoutDuplicates() {
      return this.localFiles.filter(file => {
        // Include files that either:
        // 1. Don't have valid SubID/Dataset ID (naming convention issues)
        // 2. Have valid SubID/Dataset ID but are not duplicates and not excluded
        const hasValidIds = this.isValidSubid(file.subid) && this.isValidDatasetId(file.datasetId);
        const key = hasValidIds ? `${file.subid}_${file.datasetId}` : null;
        const isDuplicate = key ? this.duplicateSubidDatasets.includes(key) : false;
        const isExcluded = this.excludedFiles.includes(file.filename);
        
        return !hasValidIds || (!isDuplicate && !isExcluded);
      });
    }
  },
  watch: {
    files: {
      handler(newFiles) {
        this.localFiles = newFiles.map(file => ({ ...file }));
        this.detectDuplicates();
        // Set initial step based on what issues exist
        this.modalStep = this.getInitialStep;
        // Initialize approved file choices if starting at review step
        if (this.modalStep === 'review-approved') {
          this.initializeApprovedFileChoices();
        }
      },
      immediate: true
    },
    validFiles: {
      handler() {
        // Initialize approved file choices when validFiles change and we're on review step
        if (this.modalStep === 'review-approved') {
          this.initializeApprovedFileChoices();
        }
      },
      immediate: true
    }
  },
  methods: {
    isValidSubid(subid) {
      if (!subid) return false;
      const subidStr = String(subid);
      return subidStr.length >= 3 && subidStr.length <= 6 && /^\d+$/.test(subidStr);
    },
    
    isValidDatasetId(datasetId) {
      if (!datasetId) return false;
      const datasetStr = String(datasetId);
      return datasetStr.length === 3 && /^\d{3}$/.test(datasetStr);
    },
    
    checkSubidDatasetConflict(subid, datasetId, currentFile) {
      if (!subid || !this.isValidSubid(subid) || !datasetId || !this.isValidDatasetId(datasetId)) return null;
      
      const key = `${subid}_${datasetId}`;
      
      // Check for conflicts in files without duplicates (naming convention section)
      const conflicts = this.filesWithoutDuplicates.filter(file => 
        file !== currentFile && 
        file.subid === subid && 
        file.datasetId === datasetId &&
        this.isValidSubid(file.subid) && 
        this.isValidDatasetId(file.datasetId)
      );
      
      // Check for conflicts in duplicate SubID-Dataset section
      if (this.duplicateSubidDatasets.includes(key)) {
        conflicts.push(...(this.filesWithDuplicates[key] || []));
      }
      
      return conflicts.length > 0 ? conflicts : null;
    },
    
    detectDuplicates() {
      // Only consider files with valid SubIDs and Dataset IDs that are not excluded
      const validFiles = this.localFiles.filter(f => 
        this.isValidSubid(f.subid) && 
        this.isValidDatasetId(f.datasetId) && 
        !this.excludedFiles.includes(f.filename)
      );
      
      const subidDatasetKeys = validFiles.map(f => `${f.subid}_${f.datasetId}`);
      const duplicateKeys = subidDatasetKeys.filter((key, index) => subidDatasetKeys.indexOf(key) !== index);
      this.duplicateSubidDatasets = [...new Set(duplicateKeys)];
      
      // Initialize subid-dataset choices for duplicates
      this.duplicateSubidDatasets.forEach(key => {
        if (!this.subidDatasetChoices[key]) {
          // Default to first file for this subid-dataset combination
          const firstFile = validFiles.find(f => `${f.subid}_${f.datasetId}` === key);
          this.subidDatasetChoices[key] = firstFile ? firstFile.filename : 'exclude';
        }
      });
    },
    
    
    proceedToDuplicates() {
      this.detectDuplicates();
      // Skip to duplicate resolution if needed, otherwise go to review
      if (this.needsDuplicateResolution) {
        this.modalStep = 'duplicate-resolution';
      } else {
        this.initializeApprovedFileChoices();
        this.modalStep = 'review-approved';
      }
    },
    
    proceedToReview() {
      this.initializeApprovedFileChoices();
      this.modalStep = 'review-approved';
    },
    
    backToFilenameCorrection() {
      this.modalStep = 'filename-correction';
    },
    
    backToDuplicateResolution() {
      // Go back to duplicate resolution if it exists, otherwise go to filename correction
      if (this.needsDuplicateResolution) {
        this.modalStep = 'duplicate-resolution';
      } else if (this.needsFilenameCorrection) {
        this.modalStep = 'filename-correction';
      } else {
        this.modalStep = 'filename-correction'; // fallback
      }
    },
    
    initializeApprovedFileChoices() {
      // Initialize all approved files as included by default
      this.approvedFiles.forEach(file => {
        this.approvedFileChoices[file.filename] = true;
      });
    },
    
    toggleApprovedFile(filename) {
      this.approvedFileChoices[filename] = !this.approvedFileChoices[filename];
    },
    
    toggleFileExclusion(filename) {
      const index = this.excludedFiles.indexOf(filename);
      if (index > -1) {
        this.excludedFiles.splice(index, 1);
      } else {
        this.excludedFiles.push(filename);
      }
    },
    
    handleFinalConfirm() {
      // Create final list of files to include
      const finalFiles = [];
      
      // Add only the approved files that are selected
      this.approvedFiles.forEach(file => {
        if (this.approvedFileChoices[file.filename]) {
          finalFiles.push(file);
        }
      });
      
      this.$emit('confirm', finalFiles);
    }
  }
}
</script>
